import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { signOut } from "supertokens-web-js/recipe/session";

import { usePostHog } from "@/external/posthog-client";
import { SESSION_IDENTITY_QUERY_KEY } from "@/queries/authentication/queryOptions";
import { routes } from "@/routes";

let signOutInProgress = false;

export function isSignOutInProgress(): boolean {
  return signOutInProgress;
}

export function useLogout(): (opts?: { keepUrl?: boolean }) => Promise<void> {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const postHog = usePostHog();

  return useCallback(
    async ({ keepUrl } = {}) => {
      if (signOutInProgress) {
        return;
      }

      signOutInProgress = true;
      try {
        if (typeof window.Intercom === "function") {
          window.Intercom("shutdown");
        }
        await signOut();
        await queryClient.cancelQueries({ queryKey: SESSION_IDENTITY_QUERY_KEY });
        queryClient.setQueryData(SESSION_IDENTITY_QUERY_KEY, null);
        // These providers stay mounted on the login route. Removing their queries
        // detaches their observers, so the next login event cannot update identity.
        queryClient.removeQueries({
          predicate: ({ queryKey: [root] }) =>
            root !== "application-config" &&
            root !== "session-credential-identity" &&
            root !== "LANGUAGES" &&
            root !== "TRANSLATIONS" &&
            root !== "CONFIGURATIONS",
        });
        queryClient.getMutationCache().clear();
        postHog?.reset(true);

        const authBase = routes.authentication.ROOT.replace("/*", "");
        const isOnAuthPage = location.pathname === authBase || location.pathname.startsWith(authBase + "/");
        if (isOnAuthPage) {
          // Session cleanup must preserve public callbacks, including their tokens.
          if (!keepUrl && location.pathname !== routes.authentication.login()) {
            await navigate(routes.authentication.login(), { replace: true });
          }
        } else {
          const currentUrl = location.pathname + location.search + location.hash;
          if (!keepUrl || !currentUrl || currentUrl === "/") {
            await navigate(routes.authentication.login());
          } else {
            await navigate(routes.authentication.login() + "?redirect=" + encodeURIComponent(currentUrl));
          }
        }
      } finally {
        signOutInProgress = false;
      }
    },
    [navigate, postHog, queryClient],
  );
}
